import type { MarketingFaqItem } from "../../shared/marketing-seo";

export interface LandingPain {
  readonly title: string;
  readonly copy: string;
}

export interface LandingSolution {
  readonly title: string;
  readonly copy: string;
}

export interface LandingContent {
  readonly path: string;
  readonly kicker: string;
  readonly h1: string;
  readonly lead: string;
  readonly painsTitle: string;
  readonly pains: readonly LandingPain[];
  readonly solutionTitle: string;
  readonly solutionLead: string;
  readonly solutions: readonly LandingSolution[];
  readonly featuresTitle: string;
  readonly features: readonly string[];
  readonly faq: readonly MarketingFaqItem[];
  readonly related: readonly string[];
  readonly ctaKicker: string;
  readonly ctaTitle: string;
  readonly ctaLead: string;
}

/**
 * Contenido editorial de las landings SEO. Módulo puro de datos: lo carga
 * App.tsx para declarar las rutas y el componente LandingPage para pintarlo.
 */
export const LANDING_CONTENT: Readonly<Record<string, LandingContent>> = {
  "/software-compraventa-coches": {
    path: "/software-compraventa-coches",
    kicker: "Software para compraventa de coches",
    h1: "Software para compraventa de coches que ordena todo el ciclo",
    lead:
      "Comprar bien, preparar rápido, publicar a tiempo y vender con margen: Cochegest es el software de gestión que acompaña cada vehículo de tu compraventa desde que entra hasta que se entrega.",
    painsTitle: "Lo que desgasta una compraventa hoy",
    pains: [
      {
        title: "La información vive dispersa",
        copy: "El precio en una hoja de cálculo, las fotos en un móvil, los papeles en una carpeta y el cliente en un WhatsApp. Cada dato existe, pero nadie lo ve entero.",
      },
      {
        title: "Los plazos se escapan",
        copy: "ITV que caduca, transferencia que no llega, reserva sin señal. No pasa todos los días, pero cuando pasa cuesta dinero y reputación.",
      },
      {
        title: "El margen se conoce tarde",
        copy: "Entre la compra, el transporte, el taller y los trámites, saber lo que deja un coche suele esperar a la venta… o a la cuenta de resultados.",
      },
    ],
    solutionTitle: "Un solo expediente por vehículo",
    solutionLead: "Cochegest convierte cada coche en un expediente vivo que ordena el trabajo de todos.",
    solutions: [
      {
        title: "Alta una sola vez",
        copy: "Matrícula, documentación, fotografías y costes entran en la misma ficha. A partir de ahí, preparación, publicación y venta leen y escriben sobre ella: nada se teclea dos veces.",
      },
      {
        title: "El día empieza con prioridades",
        copy: "Al entrar ves qué vehículos llevan demasiados días, qué documentos faltan y qué clientes esperan respuesta, cada aviso con su siguiente acción.",
      },
      {
        title: "La venta cierra el círculo",
        copy: "Reserva, contrato, cobro, entrega y transferencia cuelgan del mismo expediente. El motor fiscal está preparado técnicamente y la remisión VERI*FACTU se habilitará tras completar su activación y validación externa.",
      },
    ],
    featuresTitle: "Qué incluye el software de compraventa",
    features: [
      "Gestión de stock con estados claros: en preparación, listo, publicado, reservado, vendido",
      "Costes completos por vehículo: compra, transporte, taller y trámites",
      "CRM comercial con responsable y siguiente paso en cada consulta",
      "Escaparate online propio sincronizado con el stock",
      "Documentos y plazos: ITV, garantías, transferencias e ITP",
      "Motor fiscal VERI*FACTU preparado; remisión pendiente de activación",
      "Informes de margen, rotación y capital inmovilizado",
      "Avisos inteligentes que se cierran solos al resolverse",
    ],
    faq: [
      {
        question: "¿Sirve para una compraventa pequeña?",
        answer:
          "Sí. Cochegest está pensado para compraventas independientes: el plan de lanzamiento cubre hasta 100 vehículos y 10 personas, y el lenguaje de la aplicación habla de tu trabajo, no de departamentos.",
      },
      {
        question: "¿Tengo que cambiar mi forma de trabajar?",
        answer:
          "No. Los pasos que ya haces —comprar, preparar, publicar, vender, entregar— son los que ordena Cochegest. Lo que desaparece es la información duplicada y los plazos apuntados en papel.",
      },
      {
        question: "¿Puedo empezar sin mover todo mi histórico?",
        answer:
          "Sí. Puedes empezar por el stock actual y dar de alta vehículos nuevos a medida que entran. Cada ficha se completa con matrícula, fotografías y documentación en minutos.",
      },
    ],
    related: ["/funcionalidades", "/control-margenes", "/escaparate-online"],
    ctaKicker: "Tu stock, ordenado",
    ctaTitle: "Prueba el software con tus propios coches.",
    ctaLead: "Crear la cuenta es gratis y no pide tarjeta; el primer vehículo tarda minutos.",
  },

  "/software-concesionarios": {
    path: "/software-concesionarios",
    kicker: "Software de gestión para concesionarios",
    h1: "Software de gestión para concesionarios con equipo",
    lead:
      "Cuando en el concesionario trabajan varias personas, el problema deja de ser la información y pasa a ser la coordinación: quién hace qué, con qué permisos y con qué resultado. Cochegest lo resuelve.",
    painsTitle: "Lo que se complica al crecer",
    pains: [
      {
        title: "Nadie ve la foto completa",
        copy: "Ventas sabe de clientes, taller sabe de coches y oficina sabe de papeles. La dirección, de todo un poco y de nada del todo.",
      },
      {
        title: "Las comisiones se discuten",
        copy: "Sin un registro claro de quién cerró cada venta y con qué margen, las comisiones se calculan a mano y se repasan con desconfianza.",
      },
      {
        title: "El proceso depende de personas",
        copy: "Si falta quien lleva un expediente, la venta se para. El conocimiento vive en cabezas, no en el sistema.",
      },
    ],
    solutionTitle: "Coordinación sin reuniones de puesta al día",
    solutionLead: "Cada persona entra y ve su trabajo; la dirección entra y ve el negocio.",
    solutions: [
      {
        title: "Roles y permisos por función",
        copy: "Dirección, ventas, taller y oficina acceden cada una a sus tareas y solo a ellas. Cada cambio queda registrado con autor y fecha.",
      },
      {
        title: "Un proceso de venta compartido",
        copy: "Reserva, contrato, cobro, entrega y transferencia son estados visibles para todo el equipo: cualquiera puede continuar un expediente sin preguntar.",
      },
      {
        title: "Comisiones calculadas sobre la venta real",
        copy: "Las reglas de reparto se aplican sobre datos que ya existen en el sistema, con el detalle disponible para revisar.",
      },
      {
        title: "Informes para dirigir con números",
        copy: "Margen por vehículo, rotación de stock, capital inmovilizado y actividad comercial, listos para la reunión sin prepararla.",
      },
    ],
    featuresTitle: "Capacidades para concesionarios con equipo",
    features: [
      "Hasta 10 personas por roles dentro del plan: dirección, ventas, taller, oficina",
      "Comisiones de venta calculadas y revisables",
      "Pruebas de conducción y venta a distancia documentadas",
      "Taller con costes imputados y plazos vigilados",
      "Escaparate online y dominio propio para tu marca",
      "ITP y REBU integrados; motor VERI*FACTU preparado técnicamente",
      "Avisos escalados a la persona adecuada",
      "Historial de cambios auditable en cada expediente",
    ],
    faq: [
      {
        question: "¿Cuántas personas pueden entrar a la vez?",
        answer:
          "El plan de lanzamiento incluye hasta 10 personas, cada una con su usuario y su función. Si tu equipo es mayor, escríbenos y lo vemos contigo.",
      },
      {
        question: "¿Cada vendedor ve solo sus clientes?",
        answer:
          "Los permisos se configuran por función. La dirección ve el conjunto; cada rol trabaja sobre las tareas que le corresponden, con historial de quién hizo qué.",
      },
      {
        question: "¿Sustituye a nuestro DMS?",
        answer:
          "Cochegest cubre stock, CRM, taller, escaparate, documentos y márgenes. El motor VERI*FACTU está preparado técnicamente, pero su remisión no se ofrece como activa hasta completar declaración, configuración y validación contractual en pruebas.",
      },
    ],
    related: ["/crm-compraventa", "/control-margenes", "/precios"],
    ctaKicker: "Tu equipo, coordinado",
    ctaTitle: "Monta el espacio de tu concesionario hoy.",
    ctaLead: "Alta gratuita durante el lanzamiento, con hasta 10 personas y sin tarjeta.",
  },

  "/crm-compraventa": {
    path: "/crm-compraventa",
    kicker: "CRM para compraventas de vehículos",
    h1: "CRM para compraventas: ninguna consulta sin siguiente paso",
    lead:
      "Las consultas llegan por portales, WhatsApp, correo y teléfono. El CRM de Cochegest las convierte en oportunidades con responsable, siguiente acción y un proceso que termina en entrega, no en olvido.",
    painsTitle: "Dónde se pierden las ventas",
    pains: [
      {
        title: "Consultas que se enfrían",
        copy: "El interesado escribió hace tres días y nadie ha contestado. No por mala fe: porque la consulta vivía en el móvil de una persona.",
      },
      {
        title: "Reservas de palabra",
        copy: "«Me lo guardas, ¿no?» Sin señal ni fecha, la reserva de palabra bloquea un coche que podría estar vendiéndose a otro.",
      },
      {
        title: "El historial se evapora",
        copy: "Cuando el cliente vuelve, nadie recuerda qué se le ofreció, qué probó ni por qué no compró. Empezar de cero cada vez quema oportunidades.",
      },
    ],
    solutionTitle: "Del interés a la entrega, con pasos claros",
    solutionLead: "Cada oportunidad sabe quién la lleva, qué toca ahora y qué viene después.",
    solutions: [
      {
        title: "Toda consulta queda registrada",
        copy: "Venga de un portal, del escaparate, de WhatsApp o del teléfono, cada interesado entra en el CRM con el vehículo que le interesa y un responsable asignado.",
      },
      {
        title: "Siguiente paso siempre visible",
        copy: "Llamar, enseñar, probar, reservar: cada oportunidad muestra su próxima acción y su fecha. Los avisos escalan lo que se está enfriando.",
      },
      {
        title: "Reserva seria o coche disponible",
        copy: "Las reservas llevan señal, importe y caducidad. Si cae, el vehículo vuelve a estar disponible sin que nadie tenga que acordarse.",
      },
      {
        title: "El cierre sigue en el mismo expediente",
        copy: "Contrato, cobros, entrega y documentación se apoyan en lo que ya sabe el CRM: cliente, vehículo y condiciones pactadas.",
      },
    ],
    featuresTitle: "Qué hace el CRM de Cochegest",
    features: [
      "Registro de interesados con vehículo de interés y origen",
      "Responsable y siguiente acción con fecha en cada oportunidad",
      "Reservas con señal, importe y caducidad controlada",
      "Pruebas de conducción agendadas y documentadas",
      "Historial completo de cada cliente y cada negociación",
      "Avisos de consultas sin responder y oportunidades frías",
      "Conexión directa con contrato, cobro y entrega",
      "Escaparate online que alimenta el CRM con nuevas consultas",
    ],
    faq: [
      {
        question: "¿Puedo apuntar consultas que llegan por teléfono?",
        answer:
          "Sí. Crear una oportunidad manual lleva segundos: nombre, contacto y vehículo de interés. A partir de ahí se comporta como cualquier otra, con su responsable y su siguiente paso.",
      },
      {
        question: "¿Qué pasa si una reserva caduca?",
        answer:
          "El sistema avisa antes de la fecha y, si la reserva no se confirma, el vehículo vuelve a estar disponible para el resto del equipo y para el escaparate.",
      },
      {
        question: "¿El CRM está separado del stock?",
        answer:
          "No, y esa es la ventaja: cada oportunidad apunta a un vehículo real del stock, con su precio, su estado y su margen. No hay dos sistemas que sincronizar.",
      },
    ],
    related: ["/software-compraventa-coches", "/escaparate-online", "/funcionalidades"],
    ctaKicker: "Menos consultas perdidas",
    ctaTitle: "Da a cada interesado un siguiente paso.",
    ctaLead: "Empieza gratis y convierte la primera consulta en tu primer proceso ordenado.",
  },

  "/escaparate-online": {
    path: "/escaparate-online",
    kicker: "Escaparate online para compraventas",
    h1: "Escaparate online con tu marca y tu dominio",
    lead:
      "Publica los vehículos que elijas en una web propia, con fotografías cuidadas, formulario de contacto y bajas automáticas. Tu escaparate trabaja mientras el equipo vende.",
    painsTitle: "Por qué no basta con los portales",
    pains: [
      {
        title: "Tu marca no aparece",
        copy: "En los portales el protagonista es el portal. El cliente recuerda el coche, no a quién se lo vende.",
      },
      {
        title: "Anuncios desactualizados",
        copy: "El coche vendido que sigue publicado genera llamadas incómodas y desgasta la confianza de quien pregunta.",
      },
      {
        title: "Fotos que no venden",
        copy: "Fondos de nave, cables y sombras: la misma unidad con buena presentación se vende antes y mejor.",
      },
    ],
    solutionTitle: "Tu stock, publicado sin esfuerzo extra",
    solutionLead: "El escaparate se alimenta del propio stock: publicar no es un trabajo aparte.",
    solutions: [
      {
        title: "Publicación desde la ficha",
        copy: "Cuando un vehículo está listo, marcarlo para el escaparate es un paso dentro del flujo que ya haces, con sus fotos y sus datos de venta.",
      },
      {
        title: "Dominio propio y código QR",
        copy: "Enseña tu stock en tudominio.com y genera el QR de cada vehículo para el escaparate físico, los carteles o las tarjetas.",
      },
      {
        title: "Contacto que crea la oportunidad",
        copy: "El formulario de cada ficha no manda un correo al vacío: crea la oportunidad en el CRM con responsable y siguiente paso.",
      },
      {
        title: "Bajas automáticas",
        copy: "Lo que se vende o se reserva deja de mostrarse solo. El escaparate nunca anuncia lo que ya no está.",
      },
    ],
    featuresTitle: "Qué incluye el escaparate online",
    features: [
      "Web pública con tu nombre, tus colores y tu logo",
      "Fichas con fotografías, equipamiento, precio y garantía",
      "Fotografía asistida por IA con escenarios consistentes",
      "Dominio propio con certificado gestionado",
      "Código QR descargable por vehículo",
      "Formulario de contacto conectado al CRM",
      "Baja automática al vender o reservar",
      "Páginas pensadas para móvil y para compartir por WhatsApp",
    ],
    faq: [
      {
        question: "¿Puedo usar mi propio dominio?",
        answer:
          "Sí. Puedes apuntar el dominio de tu compraventa al escaparate; el certificado y la configuración técnica se gestionan desde la plataforma.",
      },
      {
        question: "¿Tengo que subir los anuncios dos veces?",
        answer:
          "No. El escaparate lee la ficha del vehículo: fotos, datos y precio ya existen. Publicar es decidir que ese coche se enseña, nada más.",
      },
      {
        question: "¿Qué ven los clientes cuando un coche se vende?",
        answer:
          "Deja de aparecer en el listado y su ficha pública deja de estar disponible, sin mensajes de error ni anuncios caducados.",
      },
    ],
    related: ["/crm-compraventa", "/software-compraventa-coches", "/precios"],
    ctaKicker: "Tu escaparate, siempre al día",
    ctaTitle: "Enseña tu stock con tu nombre.",
    ctaLead: "Activa el escaparate desde tu cuenta gratuita y publica el primer vehículo hoy.",
  },

  "/facturacion-verifactu": {
    path: "/facturacion-verifactu",
    kicker: "Motor VERI*FACTU para compraventas",
    h1: "Motor VERI*FACTU preparado para la activación",
    lead:
      "Cochegest incorpora un motor técnico de registros, huella y encadenamiento. La emisión y remisión VERI*FACTU permanecerán desactivadas hasta completar la declaración responsable, la configuración del proveedor y su validación técnica y contractual en pruebas.",
    painsTitle: "La fiscalidad de la compraventa, sin sustos",
    pains: [
      {
        title: "VeriFactu no admite parches",
        copy: "Facturar con un programa y «arreglarlo» con otro ya no vale: los registros deben ser íntegros, encadenados y conservados.",
      },
      {
        title: "REBU e ITP se cruzan en cada operación",
        copy: "Régimen general o REBU, ITP del comprador, exenciones… cada venta de ocasión trae decisiones fiscales que no admiten error.",
      },
      {
        title: "El papel llega tarde a la gestoría",
        copy: "Cuando la documentación se reúne a final de mes, cualquier hueco se convierte en una tarde perdida reconstruyendo operaciones.",
      },
    ],
    solutionTitle: "La factura nace dentro del expediente",
    solutionLead: "Como la venta ya sabe todo de la operación, facturar es un paso más, no otro programa.",
    solutions: [
      {
        title: "Registro verificable automático",
        copy: "El motor construye huella, encadenamiento y XML verificable. No se habilitará la emisión real hasta superar los controles de activación y transporte pendientes.",
      },
      {
        title: "REBU e ITP aplicados donde toca",
        copy: "El sistema conoce el régimen de cada vehículo y calcula el ITP de la operación, con el detalle disponible para revisar antes de emitir.",
      },
      {
        title: "Documentación fiscal siempre reunida",
        copy: "Facturas, justificantes y registros cuelgan del expediente del vehículo: la gestoría recibe información ordenada, no una bolsa de PDF.",
      },
    ],
    featuresTitle: "Base fiscal técnica y estado de activación claro",
    features: [
      "Motor de registros VERI*FACTU preparado técnicamente y desactivado por defecto",
      "Encadenamiento y huella de cada registro",
      "Régimen REBU y régimen general según el vehículo",
      "Cálculo y seguimiento del ITP por operación",
      "Rectificativas y anulaciones dentro del mismo expediente",
      "Exportación ordenada para asesoría y gestoría",
      "Avisos de plazos y documentación fiscal pendiente",
      "Historial íntegro de cada operación",
    ],
    faq: [
      {
        question: "¿Cochegest es un software VeriFactu completo?",
        answer:
          "Todavía no se presenta como servicio VERI*FACTU activo. El motor está preparado técnicamente, pero la emisión y remisión se habilitarán únicamente tras completar la declaración responsable, el endpoint/certificado o representación aplicable y las pruebas contractuales. Ese preflight no equivale a certificación ni homologación de la AEAT.",
      },
      {
        question: "¿Puedo seguir trabajando con mi gestoría?",
        answer:
          "Claro. Cochegest ordena y exporta la documentación fiscal de cada operación; tu gestoría recibe la información completa y trabaja como siempre, pero sin perseguir papeles.",
      },
      {
        question: "¿Qué pasa con el régimen REBU?",
        answer:
          "Cada vehículo lleva su régimen identificado desde la compra. Al facturar, el sistema aplica el régimen que corresponde y deja constancia en el expediente.",
      },
    ],
    related: ["/funcionalidades", "/control-margenes", "/faq"],
    ctaKicker: "VERI*FACTU, preparado con cautela",
    ctaTitle: "Organiza hoy el expediente fiscal de cada venta.",
    ctaLead: "La cuenta es gratuita durante el lanzamiento. La emisión VERI*FACTU se abrirá cuando la activación externa esté completa y verificada.",
  },

  "/control-margenes": {
    path: "/control-margenes",
    kicker: "Control de márgenes por vehículo",
    h1: "Control de márgenes: lo que deja cada coche, en tiempo real",
    lead:
      "Entre compra, transporte, taller y trámites, el margen real de un vehículo se mueve cada día. Cochegest lo calcula solo y avisa antes de que una operación deje de ser rentable.",
    painsTitle: "El margen se erosiona en silencio",
    pains: [
      {
        title: "Costes que llegan por sorpresa",
        copy: "La factura del taller, el transporte, la ITV, la comisión del portal… cada una pequeña; juntas, se comen el beneficio que creías tener.",
      },
      {
        title: "Coches que envejecen en el depósito",
        copy: "Cada día en stock cuesta dinero. Sin una señal clara de cuándo revisar el precio, la decisión llega cuando el margen ya es negativo.",
      },
      {
        title: "La rentabilidad se mide a final de mes",
        copy: "Cuando los números se juntan una vez al mes, las malas operaciones ya están consumadas y las buenas no se repiten a tiempo.",
      },
    ],
    solutionTitle: "Números que se actualizan con cada movimiento",
    solutionLead: "El margen deja de ser una estimación y pasa a ser un dato vivo.",
    solutions: [
      {
        title: "Coste real, siempre al día",
        copy: "Compra, transporte, taller, documentación y cualquier gasto imputado entran en la ficha del vehículo en cuanto ocurren.",
      },
      {
        title: "Margen previsto y real",
        copy: "Cada coche muestra lo que dejaría al precio actual y lo que dejó al venderse, con el desglose de en qué se fue la diferencia.",
      },
      {
        title: "Alertas antes de perder dinero",
        copy: "Cuando nuevos gastos dejan el margen por debajo del mínimo o un vehículo supera sus días en stock, el aviso llega con la acción sugerida.",
      },
      {
        title: "La operación completa, en una vista",
        copy: "Capital inmovilizado, rotación media y margen del conjunto: la foto que necesitas para decidir compras y precios.",
      },
    ],
    featuresTitle: "Herramientas de rentabilidad incluidas",
    features: [
      "Coste completo por vehículo: compra, transporte, taller y trámites",
      "Margen previsto al precio actual y margen real al vender",
      "Días en stock y fecha de revisión de precio sugerida",
      "Capital inmovilizado y rotación media del stock",
      "Alertas de margen mínimo y de vehículos parados",
      "Comisiones calculadas sobre el margen real de la venta",
      "Informes exportables para dirección y gestoría",
      "Objetivos de margen configurables por empresa",
    ],
    faq: [
      {
        question: "¿Los gastos del taller se descuentan del margen?",
        answer:
          "Sí. Cada orden de trabajo imputa piezas y mano de obra al vehículo, así el margen que ves ya descuenta la preparación. No hay que cruzar hojas después.",
      },
      {
        question: "¿Puedo fijar un margen mínimo de alerta?",
        answer:
          "Sí. Tu empresa define el margen mínimo aceptable y el objetivo; los avisos saltan cuando una operación los cruza, con la acción recomendada.",
      },
      {
        question: "¿Sirve si compro a particulares y a profesionales?",
        answer:
          "Sí. El régimen fiscal de cada compra —incluido REBU— forma parte del coste registrado, así que el margen refleja la operación tal como es.",
      },
    ],
    related: ["/software-compraventa-coches", "/software-concesionarios", "/funcionalidades"],
    ctaKicker: "Cada euro, localizado",
    ctaTitle: "Mira el margen de tu stock con números reales.",
    ctaLead: "Crea tu cuenta gratis y da de alta el primer vehículo con sus costes completos.",
  },
};
